import React, { useContext, useState, useEffect } from "react";
import Ctx from "../../contexts/AppContext";
import { crd, bdg, sp, rad, icBox, listRow, inpS } from "../../constants/styles";
import Fld from "../common/Fld";
import { LANGUAGES } from "../../i18n";
import { getPlanUsage } from "../../utils/plans";
import UpgradeModal from "../common/UpgradeModal";
import ChangePasswordModal from "../common/ChangePasswordModal";
import { api } from "../../api";
import { can } from "../../utils/permissions";
import { parseLegacyBankDetails } from "../../utils/bankDetails";

function SettingsPage() {
  const { t, h, lang, setLang, dk, setDk, doLogout, go, flash, pays, tens, currUserId, plan, props, refreshData, role, orgRole, settings, setSettings } = useContext(Ctx);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [billingTxns, setBillingTxns] = useState([]);
  const [showAllTxns, setShowAllTxns] = useState(false);
  // Payment & Security settings (moved from PaySettingsPage)
  const [upiName, setUpiName] = useState(settings.upi_name || "");
  const [upiId, setUpiId] = useState(settings.upi_id || "");
  // Structured bank fields (replaces the legacy free-form `bank_details`
  // textarea — tenants now see each field with its own copy button).
  // If the new fields are empty but legacy `bank_details` text exists,
  // parseLegacyBankDetails() lazy-parses the most common patterns so
  // existing landlords don't have to re-type everything after the upgrade.
  const _legacy = parseLegacyBankDetails(settings.bank_details);
  const [bankAcctName, setBankAcctName] = useState(settings.bank_account_name || _legacy.account_name || "");
  const [bankAcctNum,  setBankAcctNum]  = useState(settings.bank_account_number || _legacy.account_number || "");
  const [bankIfsc,     setBankIfsc]     = useState(settings.bank_ifsc || _legacy.ifsc || "");
  const [bankName,     setBankName]     = useState(settings.bank_name || _legacy.bank_name || "");
  const [paySettingsSaved, setPaySettingsSaved] = useState(false);
  const [showChangePwd, setShowChangePwd] = useState(false);
  const savePaySettings = () => {
    // Compose a human-readable bank_details blob too — keeps backward-
    // compat with any tenant client still on a build that reads the old
    // single-text field.
    const composed = [
      bankAcctName ? `Account Name: ${bankAcctName}` : "",
      bankAcctNum  ? `A/C No: ${bankAcctNum}` : "",
      bankIfsc     ? `IFSC: ${bankIfsc.toUpperCase()}` : "",
      bankName     ? `Bank: ${bankName}` : "",
    ].filter(Boolean).join(", ");
    const s = {
      upi_name: upiName,
      upi_id: upiId,
      bank_account_name: bankAcctName.trim(),
      bank_account_number: bankAcctNum.replace(/\s+/g, ""),
      bank_ifsc: bankIfsc.trim().toUpperCase(),
      bank_name: bankName.trim(),
      bank_details: composed,
    };
    setSettings(prev => ({ ...prev, ...s }));
    Object.keys(s).forEach(k => api.saveSetting(k, s[k]));
    setPaySettingsSaved(true);
    flash(t.settingsSaved || "Settings saved!");
    setTimeout(() => setPaySettingsSaved(false), 2000);
  };

  // Landlord Info — used to auto-populate rental-agreement PDFs.
  // Stored as a JSON blob under settings.landlord_info (same shape the
  // inline editor in AgreementPage.js uses, so both UIs stay in sync).
  const parseLLInfo = () => { try { return JSON.parse(settings?.landlord_info || '{}'); } catch { return {}; } };
  const [llInfo, setLLInfo] = useState(parseLLInfo());
  const [llSaved, setLLSaved] = useState(false);
  const setLI = (k, v) => setLLInfo(p => ({ ...p, [k]: v }));
  const saveLLInfo = async () => {
    if (!(llInfo.name || '').trim()) {
      flash("Full name is required", "error");
      return;
    }
    const blob = JSON.stringify(llInfo);
    await api.saveSetting("landlord_info", blob);
    setSettings(s => ({ ...s, landlord_info: blob }));
    setLLSaved(true);
    flash("Landlord info saved");
    setTimeout(() => setLLSaved(false), 2000);
  };
  const usage = getPlanUsage(plan.type, props.length, plan.maxProperties);
  const isPro = plan.type !== "free";
  const isMaxPlan = plan.type === "pro"; // Only Pro is the highest tier — all others can upgrade

  // Fetch billing history for landlords
  useEffect(() => {
    if (role === "landlord") {
      api.getBillingHistory().then(d => {
        if (d && d.success && d.transactions) setBillingTxns(d.transactions);
      }).catch(e => console.error("Billing history fetch error:", e));
    }
  }, [role]);

  const exportMyCsv = () => {
    const myPays = (pays || []).filter(p => p.tid === currUserId);
    if (myPays.length === 0) { flash(t.noDataExport); return; }
    const esc = (v) => { const s = String(v); return (s.includes(",") || s.includes('"') || s.includes("\n")) ? '"' + s.replace(/"/g, '""') + '"' : s; };
    const headers = ["ID", "Tenant", "Amount", "Month", "Status", "Date", "Method"];
    const rows = myPays.map(p => { const te = tens.find(x => x.id === p.tid)?.name || "Unknown"; return [p.id, te, p.amt + (p.com || 0), p.mo, p.st, p.dt || "-", p.mth || "-"].map(esc).join(","); });
    const uri = encodeURI("data:text/csv;charset=utf-8," + [headers.join(","), ...rows].join("\n"));
    const a = document.createElement("a"); a.setAttribute("href", uri); a.setAttribute("download", "my_payments.csv"); document.body.appendChild(a); a.click(); document.body.removeChild(a);
    flash(t.csvExported);
  };

  // Billing helpers
  const verifiedTxn = billingTxns.find(tx => tx.status === "verified");
  const upgradedDate = verifiedTxn ? new Date(verifiedTxn.verified_at || verifiedTxn.created_at).toLocaleDateString() : null;
  const statusChip = (st) => {
    const colors = { verified: h.gn, created: h.wn || "#f59e0b", failed: h.rd };
    const labels = { verified: t.billingVerified || "Verified", created: t.billingPending || "Pending", failed: t.billingFailed || "Failed" };
    return { bg: (colors[st] || h.sc) + "15", color: colors[st] || h.sc, label: labels[st] || st };
  };

  const items = [
    ...(can(orgRole, "team") ? [["Team Management", "\u2192", () => go("team"), "team"]] : []),
    [t.reports || "Reports & Export", "\u2192", () => go("reports"), "reports"],
    [t.dark, dk ? t.onTxt : t.offTxt, () => setDk(!dk), "settings"],
    [t.viewAgr, "\u2192", () => go("tenAgr"), "doc"],
    [t.rentActGuide || "Rent Act Guide", "\u2192", () => go("faq"), "help"],
    [t.logout, "\u2192", () => doLogout(), "logout"]
  ];

  return <div style={{ paddingTop: sp.lg }}>
    <h2 style={{ color: h.tx, fontSize: 18, fontWeight: 700, marginBottom: sp.lg, letterSpacing: "-0.2px" }}>{t.settings}</h2>

    {/* Plan card */}
    <div style={{ ...crd(h), marginBottom: sp.xs }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: sp.sm + 2 }}>
        <div style={{ display: "flex", alignItems: "center", gap: sp.sm }}>
          <div style={icBox(isMaxPlan ? h.gn : h.ac, 36)}><span style={{ fontSize: 18 }}>{isMaxPlan ? "\u2B50" : isPro ? "\uD83D\uDE80" : "\uD83D\uDCE6"}</span></div>
          <div>
            <div style={{ color: h.tx, fontWeight: 700, fontSize: 14, letterSpacing: "-0.1px" }}>{plan.name || (isPro ? "Pro Plan" : "Free Plan")}</div>
            <div style={{ color: h.ts, fontSize: 10 }}>{isPro ? `${plan.name || plan.type} Plan Active` : (t.planFreeDesc || "Basic plan")}</div>
          </div>
        </div>
        {isPro && <span style={bdg(plan.planStatus === 'expired' ? `${h.rd}15` : plan.daysRemaining != null && plan.daysRemaining <= 7 ? `${h.wn}15` : `${h.gn}15`, plan.planStatus === 'expired' ? h.rd : plan.daysRemaining != null && plan.daysRemaining <= 7 ? h.wn : h.gn)}>
        {plan.planStatus === 'expired' ? "Expired" : "\u2713 " + (t.planActive || "Active")}
      </span>}
      </div>
      {/* Plan validity — show for paid plans */}
      {isPro && plan.planEnd && <div style={{ background: plan.planStatus === 'expired' ? `${h.rd}08` : plan.daysRemaining != null && plan.daysRemaining <= 7 ? `${h.wn}08` : `${h.ac}05`, borderRadius: rad.sm, padding: `${sp.sm}px ${sp.sm + 4}px`, marginBottom: sp.sm + 2, border: `1px solid ${plan.planStatus === 'expired' ? h.rd + '20' : plan.daysRemaining != null && plan.daysRemaining <= 7 ? h.wn + '20' : h.bd}` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ color: h.ts, fontSize: 11 }}>{t.planValidity || "Plan Validity"}</span>
          <span style={{ color: plan.planStatus === 'expired' ? h.rd : plan.daysRemaining != null && plan.daysRemaining <= 7 ? h.wn : h.tx, fontWeight: 700, fontSize: 12 }}>
            {plan.daysRemaining != null ? (plan.daysRemaining > 0 ? `${plan.daysRemaining} ${t.daysLeft || "days left"}` : (t.planExpired || "Expired")) : "—"}
          </span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
          {plan.planStart && <span style={{ color: h.ts, fontSize: 10 }}>{t.from || "From"}: {new Date(plan.planStart).toLocaleDateString()}</span>}
          {plan.planEnd && <span style={{ color: h.ts, fontSize: 10 }}>{t.to || "To"}: {new Date(plan.planEnd).toLocaleDateString()}</span>}
        </div>
        {plan.planStatus === 'expired' && <div style={{ color: h.rd, fontSize: 10, fontWeight: 600, marginTop: 4 }}>{"⚠️ " + (t.renewToRestore || "Renew now to restore all features")}</div>}
        {plan.daysRemaining != null && plan.daysRemaining > 0 && plan.daysRemaining <= 7 && <div style={{ color: h.wn, fontSize: 10, fontWeight: 600, marginTop: 4 }}>{"⚠️ " + (t.planExpiringSoon || "Expiring soon — renew to continue")}</div>}
      </div>}
      {/* Property usage bar — show for all plans */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: sp.xs + 2 }}>
        <span style={{ color: h.ts, fontSize: 11 }}>{t.planPropsUsed || "Properties used"}</span>
        <span style={{ color: h.tx, fontWeight: 600, fontSize: 12 }}>{usage.used} / {usage.limit === 999999 ? "\u221E" : usage.limit}</span>
      </div>
      <div style={{ background: h.ib, borderRadius: 3, height: 6, overflow: "hidden" }}>
        <div style={{ background: usage.atLimit ? h.wn : h.ac, height: "100%", width: `${Math.min(usage.pct, 100)}%`, borderRadius: 3, transition: "width 0.4s ease" }} />
      </div>
      {usage.atLimit && !isMaxPlan && <div style={{ color: h.wn, fontSize: 10, marginTop: sp.xs + 2, fontWeight: 500 }}>{t.planAtLimit || "Property limit reached"}</div>}
      {isPro && upgradedDate && <div style={{ color: h.ts, fontSize: 10, marginTop: sp.xs + 2 }}>{t.upgradedOn || "Upgraded on"}: {upgradedDate}</div>}
      {/* Upgrade / Change Plan button — show for all plans except Pro */}
      {!isMaxPlan && <button onClick={() => setShowUpgrade(true)} style={{ marginTop: sp.sm + 2, width: "100%", padding: `${sp.sm + 2}px`, borderRadius: rad.sm, border: isPro ? `1.5px solid ${h.ac}` : "none", background: isPro ? "transparent" : (h.pp || h.ac), color: isPro ? h.ac : "#fff", cursor: "pointer", fontSize: 12, fontWeight: 700, boxShadow: isPro ? "none" : `0 2px 8px ${h.pp || h.ac}30`, transition: "all 0.2s ease", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
        {plan.planStatus === 'expired' ? ("\uD83D\uDD04 " + (t.renewPlan || "Renew Plan")) : isPro ? ("\u2B06\uFE0F " + (t.changePlan || "Change Plan")) : ("\uD83D\uDE80 " + (t.planUpgrade || "Upgrade"))}
      </button>}
      {isMaxPlan && <div style={{ marginTop: sp.sm + 2, textAlign: "center", color: h.gn, fontSize: 11, fontWeight: 600 }}>{"\u2728 " + (t.maxPlanMsg || "You're on our best plan!")}</div>}
    </div>

    {/* Billing history card */}
    {billingTxns.length > 0 && <div style={{ ...crd(h), marginBottom: sp.xs }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: sp.sm + 2 }}>
        <div style={{ color: h.tx, fontWeight: 600, fontSize: 13, letterSpacing: "-0.1px" }}>{t.billingHistory || "Billing History"}</div>
        {billingTxns.length > 2 && !showAllTxns && <span onClick={() => setShowAllTxns(true)} style={{ color: h.ac, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>{t.viewAll || "View All"} ({billingTxns.length})</span>}
        {showAllTxns && <span onClick={() => setShowAllTxns(false)} style={{ color: h.ts, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>{t.showLess || "Show Less"}</span>}
      </div>
      {(showAllTxns ? billingTxns : billingTxns.slice(0, 2)).map((tx, i, arr) => {
        const chip = statusChip(tx.status);
        const date = new Date(tx.created_at).toLocaleDateString();
        const amt = tx.amount ? `\u20B9${Math.round(tx.amount / 100)}` : "";
        return <div key={tx.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: `${sp.sm}px 0`, borderBottom: i < arr.length - 1 ? `1px solid ${h.bd}` : "none" }}>
          <div>
            <div style={{ color: h.tx, fontSize: 12, fontWeight: 500 }}>{(tx.plan_type || "").charAt(0).toUpperCase() + (tx.plan_type || "").slice(1)} Plan {amt && <span style={{ color: h.ts }}>({amt})</span>}</div>
            <div style={{ color: h.tt, fontSize: 10 }}>{date}</div>
          </div>
          <span style={bdg(chip.bg, chip.color)}>{chip.label}</span>
        </div>;
      })}
    </div>}

    {showUpgrade && <UpgradeModal h={h} t={t} used={usage.used} limit={usage.limit} currentPlan={plan.type} onClose={() => setShowUpgrade(false)} onUpgradeSuccess={async () => { await refreshData(); const hist = await api.getBillingHistory(); setBillingTxns(hist.transactions || []); flash(t.billingSuccess || "Plan Upgraded!"); }} />}

    {showChangePwd && <ChangePasswordModal h={h} t={t} onClose={() => setShowChangePwd(false)} onSuccess={(msg) => flash(msg)} />}

    {/* Landlord Info — used to auto-populate agreement PDFs.
        Reached via the '⚙️ Edit in Settings' button on the Agreement page. */}
    {role === "landlord" && (
      <div style={{ ...crd(h), marginBottom: sp.xs }}>
        <div style={{ display: "flex", alignItems: "center", gap: sp.sm, marginBottom: sp.sm + 4 }}>
          <div style={icBox(h.ac, 32)}><span style={{ fontSize: 14 }}>{"\uD83C\uDFE0"}</span></div>
          <div style={{ color: h.tx, fontWeight: 600, fontSize: 13 }}>Landlord Info (for rental agreements)</div>
        </div>
        <div style={{ color: h.sc, fontSize: 11, marginBottom: sp.sm, lineHeight: 1.4 }}>
          Used as the first party in every rental agreement you generate.
          Fill this once, reuse across all your agreements.
        </div>

        <Fld l="Full Name" v={llInfo.name || ''} set={v => setLI('name', v)} h={h} ph="e.g. Rajieev" />

        <div style={{ display: "flex", gap: sp.sm, marginBottom: sp.sm }}>
          <div style={{ flex: 2 }}>
            <Fld l="Parent / Guardian" v={llInfo.parent || ''} set={v => setLI('parent', v)} h={h} ph="e.g. Ramakrishnan" />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ color: h.ts, fontSize: 11, fontWeight: 600, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.3px" }}>Relation</label>
            <select
              value={llInfo.relation || 'son of'}
              onChange={e => setLI('relation', e.target.value)}
              style={{ ...inpS(h), fontSize: 13 }}
            >
              <option value="son of">Son of</option>
              <option value="daughter of">Daughter of</option>
              <option value="wife of">Wife of</option>
              <option value="husband of">Husband of</option>
              <option value="child of">Child of</option>
            </select>
          </div>
        </div>

        <Fld l="Aadhaar (last 4 digits)" v={llInfo.aadhaar || ''} set={v => setLI('aadhaar', String(v || '').replace(/\D/g, '').slice(-4))} h={h} ph="1234" />
        <Fld l="Address" v={llInfo.address || ''} set={v => setLI('address', v)} h={h} ph="12, Odai Street" />

        <div style={{ display: "flex", gap: sp.sm, marginBottom: sp.sm }}>
          <div style={{ flex: 1 }}>
            <Fld l="District" v={llInfo.district || ''} set={v => setLI('district', v)} h={h} ph="Virudhunagar" />
          </div>
          <div style={{ flex: 1 }}>
            <Fld l="Taluk" v={llInfo.taluk || ''} set={v => setLI('taluk', v)} h={h} ph="Sivakasi" />
          </div>
        </div>
        <Fld l="State" v={llInfo.state || 'Tamil Nadu'} set={v => setLI('state', v)} h={h} ph="Tamil Nadu" />

        <button
          onClick={saveLLInfo}
          style={{ width: "100%", padding: `${sp.sm + 4}px`, borderRadius: rad.sm, border: "none", background: llSaved ? h.gn : h.ac, color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 700, marginTop: sp.sm, transition: "background 0.2s" }}
        >
          {llSaved ? "\u2713 Saved!" : "Save Landlord Info"}
        </button>
      </div>
    )}

    {/* Payment Info — UPI & Bank Details */}
    <div style={{ ...crd(h), marginBottom: sp.xs }}>
      <div style={{ display: "flex", alignItems: "center", gap: sp.sm, marginBottom: sp.sm + 4 }}>
        <div style={icBox(h.ac, 32)}><span style={{ fontSize: 14 }}>{"\uD83D\uDCB3"}</span></div>
        <div style={{ color: h.tx, fontWeight: 600, fontSize: 13 }}>{t.paymentInfo || "Payment Info"}</div>
      </div>
      <Fld l={t.payeeName || "Payee Name"} v={upiName} set={setUpiName} h={h} ph="Your Name" />
      <Fld l={t.upiId || "UPI ID"} v={upiId} set={setUpiId} h={h} ph="username@upi" />

      {/* Structured bank-account fields. Tenants see each row with its
          own copy button on the Pay-Now screen, so they can paste
          values straight into their banking app one at a time without
          fishing them out of a single comma-separated blob. */}
      <div style={{ marginTop: sp.md, paddingTop: sp.sm, borderTop: `1px dashed ${h.bd}` }}>
        <div style={{ color: h.ts, fontSize: 11, fontWeight: 600, marginBottom: sp.sm, textTransform: "uppercase", letterSpacing: "0.3px" }}>
          {t.bankDetails || "Bank Details"} <span style={{ color: h.tt, fontWeight: 500, textTransform: "none", letterSpacing: 0 }}>(for NEFT / IMPS / RTGS)</span>
        </div>
        <Fld l="Account Holder Name" v={bankAcctName} set={setBankAcctName} h={h} ph="As per bank passbook" />
        <Fld l="Account Number"      v={bankAcctNum}  set={setBankAcctNum}  h={h} ph="617201505523" />
        <div style={{ display: "flex", gap: sp.sm }}>
          <div style={{ flex: 1 }}>
            <Fld l="IFSC"      v={bankIfsc} set={setBankIfsc} h={h} ph="ICIC0006172" />
          </div>
          <div style={{ flex: 1 }}>
            <Fld l="Bank Name" v={bankName} set={setBankName} h={h} ph="ICICI Bank" />
          </div>
        </div>
      </div>
    </div>

    {/* Save Payment */}
    <button onClick={savePaySettings} style={{ width: "100%", padding: `${sp.sm + 4}px`, borderRadius: rad.sm, border: "none", background: paySettingsSaved ? h.gn : h.ac, color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 700, marginBottom: sp.md, transition: "background 0.2s" }}>
      {paySettingsSaved ? "\u2713 Saved!" : (t.savePayment || "Save Payment Info")}
    </button>

    {/* Security — opens proper Change Password flow */}
    <div style={{ ...crd(h), marginBottom: sp.xs }}>
      <div style={{ display: "flex", alignItems: "center", gap: sp.sm, marginBottom: sp.sm + 4 }}>
        <div style={icBox(h.rd, 32)}><span style={{ fontSize: 14 }}>{"\uD83D\uDD12"}</span></div>
        <div style={{ color: h.tx, fontWeight: 600, fontSize: 13 }}>{t.security || "Security"}</div>
      </div>
      <button onClick={() => setShowChangePwd(true)} style={{ width: "100%", padding: `${sp.sm + 2}px`, borderRadius: rad.sm, border: `1px solid ${h.bd}`, background: "transparent", color: h.tx, cursor: "pointer", fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span>{t.changePassword || "Change Password"}</span>
        <span style={{ color: h.sc }}>{"\u2192"}</span>
      </button>
    </div>

    {/* Language picker */}
    <div style={{ ...crd(h) }}>
      <div style={{ color: h.tx, fontWeight: 600, fontSize: 13, marginBottom: sp.sm + 2, letterSpacing: "-0.1px" }}>{t.lang}</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: sp.sm }}>
        {LANGUAGES.map(l => <button key={l.code} onClick={() => setLang(l.code)} style={{
          padding: `${sp.sm}px ${sp.lg}px`, borderRadius: rad.md,
          border: lang === l.code ? `2px solid ${h.ac}` : `1px solid ${h.bd}`,
          background: lang === l.code ? `${h.ac}12` : "transparent",
          color: lang === l.code ? h.ac : h.tx, cursor: "pointer",
          fontSize: 13, fontWeight: lang === l.code ? 700 : 400,
          transition: "all 0.2s ease"
        }}>{l.native}</button>)}
      </div>
    </div>

    {/* Settings list */}
    <div style={{ ...crd(h), padding: 0, overflow: "hidden" }}>
      {items.map(([l, v, a, icon], i) => <div key={i} onClick={a} style={{
        ...listRow(h, i === items.length - 1),
        gap: sp.sm
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: sp.sm + 2 }}>
          <div style={icBox(l === t.logout ? h.rd : h.ac, 32)}>
            <span style={{ fontSize: 14 }}>{icon === "settings" ? "\u2699\uFE0F" : icon === "doc" ? "\uD83D\uDCC4" : icon === "help" ? "\u2753" : icon === "download" ? "\u2B07\uFE0F" : "\uD83D\uDEAA"}</span>
          </div>
          <span style={{ color: l === t.logout ? h.rd : h.tx, fontWeight: 500, fontSize: 14 }}>{l}</span>
        </div>
        <span style={{ color: l === t.logout ? h.rd : h.ac, fontWeight: 600, fontSize: 13 }}>{v}</span>
      </div>)}
    </div>
  </div>;
}

export default SettingsPage;
